import {
  LoadingSpinner,
  LocalNavigationProps,
} from '@zeiss/beyond-online-react';
import { LocalNavigation, PageHeader } from '@zeiss/lib-components';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatch, useParams } from 'react-router-dom';

import { OutletContextType, TenantActions, useTenantAuthorization } from './';
import { useTenant } from 'src/hooks/api';
import { StandardRouteParams } from 'src/types';

export const Tenant = () => {
  const { t } = useTranslation(['common']);
  const { tenantId } = useParams<StandardRouteParams>();
  const match = useMatch('/tenant-management/:id/tenants/:tenantId/:tab');

  const {
    can,
    isError: isErrorEvaluations,
    isPending: isPendingEvaluations,
  } = useTenantAuthorization(tenantId);

  const {
    data: tenantData,
    error,
    isPending,
  } = useTenant(tenantId, {
    // Only fetch once the user is confirmed authorized
    enabled: !isPendingEvaluations && !isErrorEvaluations,
  });

  if (isPending || isPendingEvaluations) {
    return (
      <LoadingSpinner label={t('common:loadingFeedback.loading')} size="xl" />
    );
  }

  if (error || isErrorEvaluations) {
    return <div>Error loading data</div>;
  }

  const localNavigationLinks: LocalNavigationProps['links'] = [
    {
      href: '',
      title: t('common:nav.details'),
    },
    {
      href: 'members',
      title: t('common:glossary.member_other'),
    },
  ];

  const tabIndex = localNavigationLinks.findIndex(
    ({ href }) => href === match?.params.tab,
  );

  const activeIndex = tabIndex === -1 ? 0 : tabIndex;

  const canDelete = can(TenantActions.DELETE);
  const canUpdateMembers = can(TenantActions.UPDATE_MEMBERS);
  const canUpdateName = can(TenantActions.UPDATE_NAME);
  const canUpdateOrganization = can(TenantActions.UPDATE_ORGANIZATION);

  return (
    <>
      <PageHeader
        eyebrow={t('common:glossary.tenant')}
        headline={tenantData.name}
      />

      <LocalNavigation activeIndex={activeIndex} links={localNavigationLinks} />

      <Outlet
        context={
          {
            canDelete,
            canUpdateMembers,
            canUpdateName,
            canUpdateOrganization,
            data: tenantData,
          } satisfies OutletContextType
        }
      />
    </>
  );
};
